import { useCallback, useEffect, useState } from 'react';
import AdminList from './AdminList';
import AdminForm from './AdminForm';
import DraftList from './DraftList';
import {
  clearCredentials,
  getDrafts,
  getMe,
  getToken,
  loginWithDeviceId,
  looksLikeJwt,
  setToken,
} from './api';
import { CATEGORIES } from './constants';

/**
 * Secret-URL admin panel for the Healife (dcp_ai_fitness) explore content.
 *
 * The credential lives in the link's hash fragment (`#k=<deviceId|jwt>`), never in
 * this bundle — the bundle is public, the link is not. Whoever holds the full link
 * can administer content; the hash is stripped from the address bar on arrival and
 * cached in localStorage so a reload keeps working.
 */
export default function DcpPanel() {
  const [status, setStatus] = useState('checking'); // checking | locked | ready
  const [error, setError] = useState(null);
  const [keyInput, setKeyInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [category, setCategory] = useState(CATEGORIES[0].key);
  const [editing, setEditing] = useState(null); // null | { item?: ExploreItem }
  const [reloadKey, setReloadKey] = useState(0);
  const [view, setView] = useState('content'); // content | drafts
  const [draftCount, setDraftCount] = useState(0);

  /** Turn a credential into a verified admin session. Returns an error message, or null. */
  const authenticate = useCallback(async (credential) => {
    try {
      if (credential) {
        if (looksLikeJwt(credential)) {
          setToken(credential);
        } else {
          await loginWithDeviceId(credential);
        }
      } else if (!getToken()) {
        return 'no-credential';
      }

      const user = await getMe();
      if (user?.isAdmin !== true) {
        clearCredentials();
        return 'Bu hesabın admin yetkisi yok.';
      }

      setStatus('ready');
      return null;
    } catch (err) {
      clearCredentials();
      return err.message;
    }
  }, []);

  // Bootstrap: prefer the key from the link, fall back to a cached session.
  useEffect(() => {
    const keyFromHash = new URLSearchParams(window.location.hash.slice(1)).get('k');
    if (keyFromHash) {
      // Drop the secret from the address bar so it doesn't linger in screenshots/history.
      window.history.replaceState(null, '', window.location.pathname);
    }

    if (!keyFromHash && !getToken()) {
      setStatus('locked');
      return;
    }

    authenticate(keyFromHash).then((message) => {
      if (!message) return;
      setStatus('locked');
      setError(message === 'no-credential' ? null : message);
    });
  }, [authenticate]);

  // Sayac sekme secilmeden de gorunmeli: taslak bekledigini haber veren tek sey bu.
  useEffect(() => {
    if (status !== 'ready') return;
    getDrafts().then((items) => setDraftCount(items.length)).catch(() => setDraftCount(0));
  }, [status, reloadKey]);

  const handleUnlock = async (event) => {
    event.preventDefault();
    if (!keyInput.trim()) return;

    setIsSubmitting(true);
    setError(null);
    const message = await authenticate(keyInput.trim());
    if (message) setError(message === 'no-credential' ? 'Anahtar gerekli.' : message);
    setIsSubmitting(false);
  };

  const handleLogout = () => {
    clearCredentials();
    setKeyInput('');
    setStatus('locked');
  };

  /**
   * The form is a view, not a route, so the browser's Back button would otherwise
   * leave the panel entirely. Opening the form pushes a history entry (same URL, so
   * the secret path stays put) and Back pops it, landing back on the list.
   */
  const openForm = (payload) => {
    window.history.pushState({ dcpForm: true }, '');
    setEditing(payload);
  };

  const closeForm = () => {
    if (window.history.state?.dcpForm) {
      window.history.back(); // popstate clears `editing`
    } else {
      setEditing(null);
    }
  };

  useEffect(() => {
    const handlePopState = () => setEditing(null);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-dcp-bg flex items-center justify-center">
        <p className="text-dcp-text-2 text-sm">Doğrulanıyor…</p>
      </div>
    );
  }

  if (status === 'locked') {
    return (
      <div className="min-h-screen bg-dcp-bg flex items-center justify-center px-4">
        <form onSubmit={handleUnlock} className="w-full max-w-sm">
          <h1 className="text-dcp-text font-bold text-xl">Admin Panel</h1>
          <p className="text-dcp-text-2 text-sm mt-1.5">
            Erişim anahtarını gir (deviceId veya token).
          </p>

          <input
            type="password"
            value={keyInput}
            onChange={(e) => setKeyInput(e.target.value)}
            autoComplete="off"
            className="mt-5 w-full bg-dcp-surface border border-dcp-border rounded-xl px-3.5 py-3 text-sm text-dcp-text outline-none focus:border-dcp-primary"
          />

          {error && <p className="mt-3 text-dcp-error text-sm">{error}</p>}

          <button
            type="submit"
            disabled={isSubmitting}
            className="mt-5 w-full h-12 rounded-2xl bg-dcp-primary text-dcp-on-primary font-bold disabled:bg-dcp-surface-variant disabled:text-dcp-text-3 transition-colors"
          >
            {isSubmitting ? 'Kontrol ediliyor…' : 'Giriş'}
          </button>
        </form>
      </div>
    );
  }

  if (editing) {
    return (
      <AdminForm
        item={editing.item}
        initialCategory={category}
        onClose={closeForm}
        onSaved={() => {
          setReloadKey((k) => k + 1);
          closeForm();
        }}
      />
    );
  }

  const tabClass = (selected) =>
    `flex-1 h-10 rounded-xl text-sm font-semibold ${
      selected ? 'bg-dcp-primary text-dcp-on-primary' : 'bg-dcp-surface text-dcp-text'
    }`;

  return (
    <div className="min-h-screen bg-dcp-bg">
      <nav className="max-w-3xl mx-auto px-4 pt-3 flex gap-2">
        <button type="button" onClick={() => setView('content')} className={tabClass(view === 'content')}>
          İçerik
        </button>
        <button type="button" onClick={() => setView('drafts')} className={tabClass(view === 'drafts')}>
          Onay bekleyenler ({draftCount})
        </button>
      </nav>
      {view === 'drafts' ? (
        <DraftList
          key={reloadKey}
          onEdit={(item) => openForm({ item })}
          onCountChange={setDraftCount}
        />
      ) : (
        <AdminList
          key={`${category}-${reloadKey}`}
          category={category}
          onSelectCategory={setCategory}
          onCreate={() => openForm({})}
          onEdit={(item) => openForm({ item })}
          onLogout={handleLogout}
        />
      )}
    </div>
  );
}
